(() => {
  'use strict';
  const tag = 'wb-definition-product';
  if (customElements.get(tag)) return;
  const NS = 'http://www.w3.org/2000/svg';
  class DefinitionProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const configEl = this.querySelector('[data-config]');
      if (!configEl) { requestAnimationFrame(() => this.connectedCallback()); return; }
      this.initialized = true;
      this.abort = new AbortController();
      try { this.config = JSON.parse(configEl.textContent); }
      catch (_) { this.initialized = false; return; }
      this.form = this.querySelector('form');
      this.photo = this.querySelector('[data-main-photo]');
      this.overlay = this.querySelector('[data-overlay]');
      this.panel = this.querySelector('[data-custom]');
      this.status = this.querySelector('[data-status]');
      this.caption = this.querySelector('[data-caption]');
      this.quote = this.querySelector('[data-quote]');
      this.add = this.querySelector('[data-add]');
      this.confirm = this.querySelector('[data-confirm]');
      this.fields = [...this.querySelectorAll('[data-field]')];
      this.ctx = document.createElement('canvas').getContext('2d');
      this.view = 'guide';
      this.busy = false;
      const on = (el, event, fn) => el?.addEventListener(event, fn, {signal: this.abort.signal});
      this.querySelectorAll('[data-choice]').forEach(el => on(el, 'change', () => this.select(el.value)));
      this.querySelectorAll('[data-select]').forEach(el => on(el, 'click', () => this.select(el.dataset.select)));
      this.querySelectorAll('[data-card-select]').forEach(el => on(el, 'click', () => {
        this.select(el.dataset.cardSelect);
        this.scrollIntoView({behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block:'start'});
      }));
      on(this.querySelector('[data-open-guide]'), 'click', () => {
        const custom = this.config.variants.find(v => v.preset?.custom);
        if (custom) this.select(custom.id);
        this.view = 'guide'; this.render();
      });
      this.querySelectorAll('[data-view]').forEach(el => on(el, 'click', () => {
        this.view = el.dataset.view; this.render();
      }));
      this.fields.forEach(input => {
        on(input, 'input', () => {
          input.dataset.pasteError = '';
          input.dataset.touched = 'true';
          this.confirm.checked = false;
          this.render();
        });
        on(input, 'blur', () => { input.dataset.touched = 'true'; this.render(); });
        on(input, 'paste', event => {
          const text = event.clipboardData?.getData('text') || '';
          const result = input.value.slice(0, input.selectionStart) + text + input.value.slice(input.selectionEnd);
          if (/[\r\n\t]/.test(text) || result.length > input.maxLength) {
            event.preventDefault();
            input.dataset.touched = 'true';
            input.dataset.pasteError = `Paste one line of up to ${input.maxLength} characters. Your existing text has not changed.`;
            this.render();
          }
        });
      });
      on(this.confirm, 'change', () => this.render());
      on(this.form, 'submit', event => this.submit(event));
      const requested = new URLSearchParams(location.search).get('variant');
      this.select(this.config.variants.some(v => String(v.id) === requested) ? requested : this.config.selectedVariantId, false);
      document.fonts?.ready.then(() => { if (this.isConnected) this.render(); });
    }
    disconnectedCallback() { this.abort?.abort(); this.initialized = false; }
    select(id, updateUrl = true) {
      const variant = this.config.variants.find(v => String(v.id) === String(id));
      if (!variant) { this.message('This definition is unavailable. Please choose another version.', 'error'); return; }
      this.active = variant;
      this.view = 'guide';
      this.querySelectorAll('[data-choice]').forEach(el => { el.checked = String(el.value) === String(id); });
      this.querySelectorAll('[data-select]').forEach(el => el.setAttribute('aria-pressed', String(String(el.dataset.select) === String(id))));
      this.querySelector('[data-open-guide]')?.setAttribute('aria-pressed', 'false');
      const variantInput = this.form.querySelector('[name="id"]');
      variantInput.value = id;
      variantInput.dispatchEvent(new Event('change', {bubbles:true}));
      this.querySelectorAll('[data-price]').forEach(el => { el.textContent = variant.priceText || ''; });
      this.panel.hidden = !variant.preset?.custom;
      this.panel.disabled = !variant.preset?.custom;
      this.querySelector('[data-preview-tabs]').hidden = !variant.preset?.custom;
      this.quote.hidden = !!variant.preset?.custom;
      if (updateUrl && /^https?:$/.test(location.protocol)) {
        const url = new URL(location.href); url.searchParams.set('variant', id);
        history.replaceState(history.state, '', url);
      }
      this.render();
      this.dispatchEvent(new CustomEvent('wb:definition-change', {bubbles:true, detail:{variantId:String(id), custom:!!variant.preset?.custom}}));
    }
    message(text, state = 'info') { this.status.textContent = text; this.status.dataset.state = state; }
    values() {
      const vals = Object.fromEntries(this.fields.map(f => [f.dataset.field, f.value]));
      const first = (vals.title.trim()[0] || '');
      vals.autoInitials = vals.initials || first.toUpperCase() + first.toLowerCase();
      return vals;
    }
    measure(text, size, italic = false) {
      if (!this.ctx) return Infinity;
      this.ctx.font = `${italic ? 'italic ' : ''}${size}px "Times New Roman", Times, serif`;
      const m = this.ctx.measureText(text);
      return Math.max(m.width, (m.actualBoundingBoxLeft || 0) + (m.actualBoundingBoxRight || m.width));
    }
    validate(showAll = false) {
      const errors = [];
      this.fields.forEach(input => {
        const key = input.dataset.field, val = input.value;
        let error = input.dataset.pasteError || '';
        if (!error && input.required && !val.trim()) error = key === 'title' ? 'Please enter a print title.' : 'Please enter your first definition line.';
        if (!error && val.length > input.maxLength) error = `Use no more than ${input.maxLength} characters, including spaces.`;
        if (!error && /[\r\n\t\u0000-\u001f]/.test(val)) error = 'Each box must contain one printed line only.';
        const size = key === 'title' ? 86 : key === 'seeAlso' ? 27 : key === 'initials' ? 66 : 29;
        const text = key === 'seeAlso' && val ? 'see also: ' + val : val;
        const width = key === 'initials' ? 76 : 430;
        if (!error && this.measure(text, size, key === 'seeAlso') > width) error = 'This wording is too wide. Please shorten it; it will not be wrapped or shrunk.';
        const wrapper = input.closest('.wd-field');
        wrapper.querySelector('[data-count]').textContent = `${val.length}/${input.maxLength}`;
        const show = !!error && (showAll || input.dataset.touched === 'true');
        input.setCustomValidity(error);
        input.setAttribute('aria-invalid', String(show));
        wrapper.classList.toggle('is-invalid', show);
        wrapper.querySelector('[data-error]').textContent = show ? error : '';
        if (error) errors.push({key, message:error, input});
      });
      return errors;
    }
    svgText(x, y, value, size, italic = false) {
      if (!value) return;
      const el = document.createElementNS(NS, 'text');
      Object.entries({x,y,'font-size':size,'font-family':'Times New Roman, Times, serif',fill:'#0c0c0b'}).forEach(([k,v]) => el.setAttribute(k,String(v)));
      if (italic) el.setAttribute('font-style','italic');
      el.setAttribute('xml:space','preserve'); el.style.whiteSpace = 'pre';
      el.textContent = value; this.overlay.appendChild(el);
    }
    renderOverlay(vals) {
      this.overlay.replaceChildren();
      const line = document.createElementNS(NS,'line');
      Object.entries({x1:399,x2:399,y1:314,y2:988,stroke:'#0e0e0d','stroke-width':2.7}).forEach(([k,v]) => line.setAttribute(k,String(v)));
      this.overlay.appendChild(line);
      this.svgText(316,366,vals.autoInitials,66);
      this.svgText(418,601,vals.title,86);
      this.svgText(418,666,'(noun)',46,true);
      for (let i=1;i<=6;i++) this.svgText(418,751+34*(i-1),vals['line'+i],29);
      if (vals.seeAlso) this.svgText(418,960,'see also: '+vals.seeAlso,27,true);
    }
    syncProperties(vals, custom) {
      const preset = this.active.preset;
      if (!preset) return;
      const lines = custom ? Array.from({length:6},(_,i) => vals['line'+(i+1)] || '') : [...preset.lines];
      const title = custom ? vals.title : preset.title;
      const initials = custom ? vals.autoInitials : preset.initials;
      const seeAlso = custom ? vals.seeAlso : preset.seeAlso;
      const props = {'Definition choice':this.active.name,'Print title':title,'Initials':initials,'Word type':'(noun)','Overall framed size':this.config.overallSize,'Frame':'Included'};
      lines.forEach((line,i) => { props['Definition line '+(i+1)] = line; });
      if (seeAlso) props['See also'] = seeAlso;
      if (custom && this.confirm.checked) props['Wording checked'] = 'Yes — spelling, punctuation and line order';
      props['_Definition layout'] = JSON.stringify({version:2,variantId:String(this.active.id),custom,title,initials,wordType:'noun',lines,seeAlso:seeAlso||'',lineBreaks:'preserve',overallSize:this.config.overallSize});
      const host = this.querySelector('[data-properties]'); host.replaceChildren();
      for (const [name,value] of Object.entries(props)) {
        const input = document.createElement('input'); input.type='hidden'; input.name=`properties[${name}]`; input.value=value; host.appendChild(input);
      }
    }
    render() {
      if (!this.active) return;
      if (!this.active.preset) { this.add.disabled = true; this.message('Definition artwork is not configured yet. Please contact Wolfborn Studio.','error'); return; }
      const custom = !!this.active.preset.custom;
      let valid = true;
      if (custom) {
        const vals = this.values(), errors = this.validate();
        valid = errors.length === 0 && this.confirm.checked;
        const hasBody = Array.from({length:6},(_,i) => vals['line'+(i+1)]).some(Boolean);
        const useGuide = this.view === 'guide' || !hasBody;
        this.photo.src = useGuide ? (this.config.guideUrl || this.active.image) : this.config.blankUrl;
        this.photo.alt = useGuide ? 'Custom wording guide: title, initials and six individual definition lines. Labels are not printed.' : 'Your custom wording layout on clear acrylic.';
        this.overlay.hidden = useGuide;
        this.querySelectorAll('[data-view]').forEach(btn => btn.setAttribute('aria-pressed', String(btn.dataset.view === (useGuide?'guide':'wording'))));
        this.caption.textContent = useGuide ? 'Use this example to see where each field goes. Arrows and labels are not printed.' : 'Your wording — layout guide. Please check each line before ordering.';
        if (!useGuide) this.renderOverlay(vals);
        this.syncProperties(vals, true);
        const shownError = errors.find(e => e.input.dataset.touched === 'true');
        if (shownError) this.message(shownError.message, 'error');
        else if (errors.length) this.message('Start with your title and first definition line. The labelled example stays here while you fill in your wording.');
        else if (!this.confirm.checked) this.message('Your wording fits the layout. Tick the wording check before adding it to your cart.');
        else this.message('Your wording and line order are ready. Guide labels will not be printed.','success');
      } else {
        this.fields.forEach(input => { input.setCustomValidity(''); });
        this.photo.src = this.active.image;
        this.photo.alt = this.active.name + ' — framed clear acrylic definition print.';
        this.overlay.hidden = true;
        this.caption.textContent = this.active.name;
        this.quote.textContent = this.active.preset.lines.join(' ') + (this.active.preset.seeAlso ? '\n\nSee also: ' + this.active.preset.seeAlso : '');
        this.syncProperties({}, false);
        this.message('This version uses the wording shown above. The frame is included.');
      }
      this.add.disabled = this.busy || this.config.reviewMode || !this.active.available || !valid;
      this.add.textContent = this.busy ? 'Adding…' : this.config.reviewMode ? 'Preview only — not yet for sale' : !this.active.available ? 'Currently unavailable' : 'Add to cart';
    }
    async submit(event) {
      event.preventDefault();
      if (this.busy) return;
      if (this.config.reviewMode) { this.message('This product is in review mode and cannot be purchased yet.','info'); return; }
      if (!this.active?.available || !this.active.preset) { this.message('This version is currently unavailable.','error'); return; }
      if (this.active.preset.custom) {
        const errors = this.validate(true);
        if (errors.length) { errors[0].input.focus(); this.message(errors[0].message,'error'); return; }
        if (!this.confirm.checked) { this.confirm.focus(); this.message('Please check your spelling and line order, then tick the wording check.','error'); return; }
      }
      const quantity = this.form.querySelector('[name="quantity"]');
      if (!quantity.checkValidity() || !Number.isSafeInteger(Number(quantity.value)) || Number(quantity.value)<1) { quantity.reportValidity(); return; }
      this.syncProperties(this.active.preset.custom ? this.values() : {}, !!this.active.preset.custom);
      const formData = new FormData(this.form);
      // Only submit actual Shopify variant, quantity and line-item properties.
      for (const key of [...formData.keys()]) if (key.startsWith('wd-definition-')) formData.delete(key);
      this.busy = true; this.render();
      const timeout = new AbortController();
      const timer = setTimeout(() => timeout.abort(), 20000);
      try {
        const response = await fetch(this.config.cartAddUrl, {method:'POST',body:formData,headers:{Accept:'application/json'},signal:timeout.signal});
        const data = await response.json();
        if (!response.ok || data.status >= 400) throw new Error(data.description || data.message || 'The item could not be added. Please try again.');
        window.location.assign(this.config.cartUrl);
      } catch (error) {
        this.busy = false; this.render();
        this.message(error.name==='AbortError' ? 'The store took too long to respond. Check your cart before trying again.' : error.message || 'Connection error. Check your cart before trying again.','error');
      } finally { clearTimeout(timer); }
    }
  }
  customElements.define(tag, DefinitionProduct);
})();
